import React from 'react';
import { MMMConfigFormState, ControlVar } from '../../types/mmmConfig';
import { TextInput, Select } from '../FormFields';

interface ControlVariablesSectionProps {
  config: MMMConfigFormState;
  dataColumns: string[];
  onChange: (config: Partial<MMMConfigFormState>) => void;
  errors: Record<string, string | undefined>;
}

export const ControlVariablesSection: React.FC<ControlVariablesSectionProps> = ({
  config,
  dataColumns,
  onChange,
  errors,
}) => {
  const handleAddVar = () => {
    const newVars = [...(config.control_vars || [])];
    newVars.push({ name: '', column: '' });
    onChange({ control_vars: newVars });
  };

  const handleRemoveVar = (index: number) => {
    const newVars = config.control_vars?.filter((_, i) => i !== index) || [];
    onChange({ control_vars: newVars });
  };

  const handleVarChange = <K extends keyof ControlVar>(
    index: number,
    field: K,
    value: ControlVar[K]
  ) => {
    const newVars = [...(config.control_vars || [])];
    newVars[index] = { ...newVars[index], [field]: value };

    if (field === 'column' && value && !newVars[index].name) {
      newVars[index].name = value as string;
    }

    onChange({ control_vars: newVars });
  };

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Control Variables</h2>
        <button onClick={handleAddVar} className="btn btn-success text-sm">
          + Add Variable
        </button>
      </div>

      {(!config.control_vars || config.control_vars.length === 0) ? (
        <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 text-center">
          <p className="text-gray-600 font-medium">No control variables added</p>
          <p className="text-gray-500 text-sm mt-1">These help account for non-marketing factors</p>
        </div>
      ) : (
        <div className="space-y-3">
          {config.control_vars.map((variable, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex gap-4">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextInput
                    name={`control_${index}_name`}
                    label="Variable Name"
                    value={variable.name}
                    onChange={(val) => handleVarChange(index, 'name', val)}
                    placeholder="e.g., price_index"
                    required
                    error={errors[`control_vars.${index}.name`]}
                  />

                  <Select
                    name={`control_${index}_column`}
                    label="Data Column"
                    value={variable.column}
                    onChange={(val) => handleVarChange(index, 'column', val)}
                    options={dataColumns.map((col) => ({ value: col, label: col }))}
                    required
                    placeholder="Select column..."
                    error={errors[`control_vars.${index}.column`]}
                  />
                </div>
                <button
                  onClick={() => handleRemoveVar(index)}
                  className="btn btn-danger text-xs h-fit mt-6"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="info-box">
        <h3 className="info-title">About Control Variables</h3>
        <ul>
          <li>Account for non-marketing factors affecting your target</li>
          <li>Examples: price, promotions, holidays, seasonality indices</li>
          <li>Optional - include only if you have relevant data</li>
          <li>No adstock/saturation transformations applied</li>
        </ul>
      </div>
    </div>
  );
};
